import { Controller } from "@hotwired/stimulus"

// The seat plan editor page. Keeps the unassigned-guest count fresh, narrows
// the unassigned list by search text and the Dietary / Notes chips, and opens
// the auto-assign modal.
export default class extends Controller {
  static targets = ["tablesList", "unassignedList", "unassignedCount", "searchInput", "filterChip", "searchEmpty"]
  static values = { eventId: Number }

  static activeChipClasses = ["bg-teal-500", "text-white", "border-teal-500", "hover:bg-teal-600"]
  static inactiveChipClasses = ["bg-gray-100", "text-gray-700", "border-gray-200", "hover:bg-gray-200"]

  connect() {
    this.currentFilter = null
    this.query = ""
    this.boundUpdateUnassignedCount = () => this.updateUnassignedCount()
    this.setupGlobalDragHandlers()
    this.observeUnassignedList()
  }

  disconnect() {
    document.removeEventListener('guest:assigned', this.boundUpdateUnassignedCount)
    document.removeEventListener('guest:unassigned', this.boundUpdateUnassignedCount)
    this.listObserver?.disconnect()
  }

  setupGlobalDragHandlers() {
    document.addEventListener('guest:assigned', this.boundUpdateUnassignedCount)
    document.addEventListener('guest:unassigned', this.boundUpdateUnassignedCount)
  }

  // Turbo Streams swap the list's rows out whenever a seat changes, so the
  // search text and chips are re-applied to whatever rows arrive.
  observeUnassignedList() {
    if (!this.hasUnassignedListTarget) return
    this.listObserver = new MutationObserver(() => this.applyFilters())
    this.listObserver.observe(this.unassignedListTarget, { childList: true })
  }

  search() {
    this.query = this.searchInputTarget.value.trim().toLowerCase()
    this.applyFilters()
  }

  filterDietary() {
    this.toggleFilter('dietary')
  }

  filterNotes() {
    this.toggleFilter('notes')
  }

  toggleFilter(name) {
    this.currentFilter = this.currentFilter === name ? null : name
    this.updateChips()
    this.applyFilters()
  }

  updateChips() {
    this.filterChipTargets.forEach(chip => {
      const active = chip.dataset.filter === this.currentFilter
      chip.classList.remove(...(active ? this.constructor.inactiveChipClasses : this.constructor.activeChipClasses))
      chip.classList.add(...(active ? this.constructor.activeChipClasses : this.constructor.inactiveChipClasses))
      chip.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
  }

  applyFilters() {
    if (!this.hasUnassignedListTarget) return
    const guests = this.unassignedListTarget.querySelectorAll('[data-controller~="guest-card"]')
    let visible = 0

    guests.forEach(guestEl => {
      const show = this.shouldShowGuest(guestEl)
      guestEl.style.display = show ? '' : 'none'
      if (show) visible += 1
    })

    if (this.hasSearchEmptyTarget) {
      this.searchEmptyTarget.classList.toggle('hidden', guests.length === 0 || visible > 0)
    }
  }

  shouldShowGuest(guestElement) {
    return this.matchesQuery(guestElement) && this.matchesFilter(guestElement)
  }

  matchesQuery(guestElement) {
    if (!this.query) return true
    return (guestElement.dataset.name || '').includes(this.query)
  }

  matchesFilter(guestElement) {
    if (!this.currentFilter) return true

    if (this.currentFilter === 'dietary') {
      return guestElement.querySelector('.ph-leaf') !== null
    } else if (this.currentFilter === 'notes') {
      return guestElement.querySelector('.ph-note') !== null
    }

    return true
  }

  showAutoAssign(event) {
    event.preventDefault()
    // Open auto-assign modal
    const modal = document.getElementById('auto-assign-modal')
    const url = `/events/${this.eventIdValue}/seat_assignments`

    // Reset src first so Turbo treats re-assignment as a new request
    modal.removeAttribute('src')
    modal.src = url
  }

  updateUnassignedCount() {
    // Fetch updated count via Turbo
    fetch(`/events/${this.eventIdValue}/unassigned_guests_count`)
      .then(response => response.text())
      .then(count => {
        this.unassignedCountTarget.textContent = count
      })
  }
}
